import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Tooltip, TextField } from "@mui/material";
import Swal from "sweetalert2";
import { NumericFormat } from "react-number-format";

import {
  currentTenant,
  thousandsdisplay,
  userid,
} from "../../Common/Credentials.js";
import { spin } from "../../Common/NewLoader.jsx";
import { appmasterid } from "../../Common/Permissions.js";
import Header from "../../Component/Header.js";
import Menu from "../../Component/Menu.js";
import Footer from "../../Component/Footer.js";
import CustomerTermsServices from "./CustomerTermsServices.js";
import UsercustomerService from "../Usercustomers/UsercustomerService.js";

import EditIcon from "../../images/Icons/edit.png";
import CheckIcon from "../../images/Icons/check.png";
import CloseIcon from "../../images/Icons/clear1.png";
import UpArrow from "../../images/Icons/up-arrow.png";
import Breadcrumb from "../../NewUI/NewComponents/Breadcrumb.jsx";

// TabButton component

const TabButton = ({ active, onClick, children }) => (
  <fieldset
    onClick={onClick}
    className={`UI-tab-button ${active ? "UI-active-tab" : "UI-inactive-tab"}`}
    style={{
      cursor: "pointer",
      transition: "all 0.3s ease",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "6px",
      padding: "8px 16px",
      fontSize: "0.8rem",
      fontWeight: 500,
      textWrap: "nowrap",
    }}
  >
    {children}
    <img
      src={UpArrow}
      alt="Up Arrow"
      className="UI-tab-arrow"
      style={{
        transform: active ? "rotate(30deg)" : "rotate(0deg)",
      }}
    />
  </fieldset>
);

const CustomerTermsData = () => {
  const [customerTerms, setCustomerTerms] = useState([]);
  const [filteredTerms, setFilteredTerms] = useState([]);
  const [creditRequests, setCreditRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("customer-terms");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchTermRequests, setSearchTermRequests] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [hoveredAction, setHoveredAction] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageRequests, setCurrentPageRequests] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [usercustomers, setUsercustomers] = useState([]);
  const [approveState, setApproveState] = useState({});
  const [creditValues, setCreditValues] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: 'customer', direction: 'asc' });
  const [sortConfigRequests, setSortConfigRequests] = useState({ key: 'customer', direction: 'asc' });
  const navigate = useNavigate();

  // Search highlighting methods
  const escapeRegex = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const highlightSearchText = (text, searchTerm) => {
    if (!searchTerm.trim() || !text) return text;

    const regex = new RegExp(`(${escapeRegex(searchTerm)})`, 'gi');
    const parts = String(text).split(regex);

    return parts.map((part, index) =>
      regex.test(part)
        ? React.createElement('mark', {
          key: index,
          className: 'UI-search-highlight'
        }, part)
        : part
    );
  };

  // Handle resize
  const handleResize = () => {
    const mobile = window.innerWidth <= 768;
    setIsMobile(mobile);
    if (!mobile) {
      setShowSearch(false);
    }
  };

  const refreshData = useCallback(() => {
    setLoading(true);

    // Load user customers first
    UsercustomerService.GetUserCustomersbyUser(userid)
      .then((res) => {
        const userCustomers = res.data === "" || res.data === undefined
          ? []
          : res.data.customers.split(",");
        setUsercustomers(userCustomers);

        // Load customer terms
        CustomerTermsServices.retriveCustomerTerms(currentTenant)
          .then((response) => {
            const filteredTerms = response.data.filter((e) =>
              userCustomers.length === 0 ? e : userCustomers.includes(e.customer)
            );
            const sortedTerms = filteredTerms.sort((a, b) => a.customer?.localeCompare(b.customer));
            setCustomerTerms(sortedTerms);
            setFilteredTerms(sortedTerms);
          })
          .catch((e) => {
            if (e.response?.data?.error === "Unauthorized") {
              Swal.fire({
                title: "Your Session is Lost: Please login Back to resume",
                text: ".",
                icon: "error",
                confirmButtonText: "Ok",
                confirmButtonColor: "#152f5f",
              }).then(() => navigate("/"));
            }
          });

        // Load credit requests
        CustomerTermsServices.GetAllRequestCredits(currentTenant)
          .then((response) => {
            const filteredRequests = response.data
              .filter((e) =>
                userCustomers.length === 0 ? e : userCustomers.includes(e.customer)
              )
              .map((e) => Object.assign(e, { approvestate: false }));

            const sortedRequests = filteredRequests.sort((a, b) => a.customer?.localeCompare(b.customer));
            setCreditRequests(sortedRequests);
            setFilteredRequests(sortedRequests);

            // Initialize credit values with request values
            const initialCreditValues = {};
            sortedRequests.forEach(req => {
              initialCreditValues[req.id] = req.reqvalue;
            });
            setCreditValues(initialCreditValues);
          })
          .catch((e) => {
            if (e.response?.data?.error === "Unauthorized") {
              Swal.fire({
                title: "Your Session is Lost: Please login Back to resume",
                text: ".",
                icon: "error",
                confirmButtonText: "Ok",
                confirmButtonColor: "#152f5f",
              }).then(() => navigate("/"));
            }
          });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [currentTenant, navigate, userid]);

  useEffect(() => {
    if (window.innerWidth <= 768) {
      setShowSearch(true);
    }
    window.addEventListener('resize', handleResize);

    if (!userid) {
      window.confirm("Your Session is Lost: Please login Back to resume");
      navigate("/");
      return;
    }

    refreshData();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [navigate, refreshData]);

  useEffect(() => {
    updateFilteredTerms();
  }, [customerTerms, searchTerm]);

  useEffect(() => {
    updateFilteredRequests();
  }, [creditRequests, searchTermRequests]);

  // Sorting function for Customer Terms
  const sortTerms = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedTerms = (terms) => {
    if (!sortConfig.key) return terms;

    return [...terms].sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case 'customer':
          aValue = a.customer || '';
          bValue = b.customer || '';
          break;
        case 'creditvalue':
          aValue = parseFloat(a.creditvalue) || 0;
          bValue = parseFloat(b.creditvalue) || 0;
          break;
        case 'creditleft':
          aValue = parseFloat(a.creditleft) || 0;
          bValue = parseFloat(b.creditleft) || 0;
          break;
        case 'creditperiod':
          aValue = a.creditperiod || '';
          bValue = b.creditperiod || '';
          break;
        case 'creditstatus':
          aValue = a.creditstatus || '';
          bValue = b.creditstatus || '';
          break;
        default:
          aValue = a[sortConfig.key] || '';
          bValue = b[sortConfig.key] || '';
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  // Sorting function for Credit Requests
  const sortRequests = (key) => {
    let direction = 'asc';
    if (sortConfigRequests.key === key && sortConfigRequests.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfigRequests({ key, direction });
  };

  const getSortedRequests = (requests) => {
    if (!sortConfigRequests.key) return requests;

    return [...requests].sort((a, b) => {
      let aValue, bValue;

      switch (sortConfigRequests.key) {
        case 'customer':
          aValue = a.customer || '';
          bValue = b.customer || '';
          break;
        case 'reqvalue':
          aValue = parseFloat(a.reqvalue) || 0;
          bValue = parseFloat(b.reqvalue) || 0;
          break;
        case 'requestedby':
          aValue = a.requestedby || '';
          bValue = b.requestedby || '';
          break;
        case 'creditvalue':
          aValue = parseFloat(a.creditvalue) || 0;
          bValue = parseFloat(b.creditvalue) || 0;
          break;
        case 'approvedby':
          aValue = a.approvedby || '';
          bValue = b.approvedby || '';
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        default:
          aValue = a[sortConfigRequests.key] || '';
          bValue = b[sortConfigRequests.key] || '';
      }

      if (aValue < bValue) {
        return sortConfigRequests.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfigRequests.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const updateFilteredTerms = () => {
    let filtered;
    if (!searchTerm.trim()) {
      filtered = customerTerms;
    } else {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = customerTerms.filter(term =>
        term.customer?.toLowerCase().includes(lowerSearchTerm) ||
        term.creditstatus?.toLowerCase().includes(lowerSearchTerm) ||
        term.creditperiod?.toLowerCase().includes(lowerSearchTerm)
      );
    }
    setFilteredTerms(filtered);
    setCurrentPage(1);
  };

  const updateFilteredRequests = () => {
    let filtered;
    if (!searchTermRequests.trim()) {
      filtered = creditRequests;
    } else {
      const lowerSearchTerm = searchTermRequests.toLowerCase();
      filtered = creditRequests.filter(request =>
        request.customer?.toLowerCase().includes(lowerSearchTerm) ||
        request.requestedby?.toLowerCase().includes(lowerSearchTerm) ||
        request.status?.toLowerCase().includes(lowerSearchTerm) ||
        (request.approvedby && request.approvedby.toLowerCase().includes(lowerSearchTerm))
      );
    }
    setFilteredRequests(filtered);
    setCurrentPageRequests(1);
  };

  // Get current page data for pagination
  const getCurrentPageData = () => {
    const sortedTerms = getSortedTerms(filteredTerms);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return sortedTerms.slice(indexOfFirstItem, indexOfLastItem);
  };

  const getCurrentPageRequestsData = () => {
    const sortedRequests = getSortedRequests(filteredRequests);
    const indexOfLastItem = currentPageRequests * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return sortedRequests.slice(indexOfFirstItem, indexOfLastItem);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageChangeRequests = (newPage) => {
    setCurrentPageRequests(newPage);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
    setCurrentPageRequests(1);
  };

  const handleSearch = (term) => {
    if (activeTab === "customer-terms") {
      setSearchTerm(term);
    } else {
      setSearchTermRequests(term);
    }
  };

  const handleApprove = (id) => {
    setApproveState(prev => ({
      ...prev,
      [id]: true
    }));
  };

  const handleClose = (id) => {
    setApproveState(prev => ({
      ...prev,
      [id]: false
    }));
  };

  const handleCreditValueChange = (id, value) => {
    setCreditValues(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleApproveCredit = (id) => {
    const request = creditRequests.find(req => req.id === id);
    if (!request) return;

    const creditValue = creditValues[id] || request.reqvalue;

    if (creditValue > request.reqvalue) {
      Swal.fire({
        title: "Credit Value Error",
        text: "Approved credit value cannot exceed requested value",
        icon: "error",
        confirmButtonText: "Ok",
        confirmButtonColor: "#152f5f",
      });
      return;
    }

    const data = {
      id: id,
      customer: request.customer,
      custid: request.custid,
      approvedby: userid,
      tenantid: currentTenant,
      creditvalue: creditValue
    };

    Swal.fire({
      title: "Approve Credit Request?",
      text: `Are you sure you want to approve ${creditValue} credit for ${request.customer}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes",
      confirmButtonColor: "#152f5f",
      cancelButtonText: "No",
    }).then((result) => {
      if (result.value) {
        CustomerTermsServices.UpdateRequestedCredit(data)
          .then(() => {
            Swal.fire({
              title: "Credit Approved Successfully",
              text: ".",
              icon: "success",
              confirmButtonText: "Ok",
              confirmButtonColor: "#152f5f",
            }).then(() => {
              refreshData();
              setApproveState(prev => ({ ...prev, [id]: false }));
            });
          })
          .catch((e) => {
            if (e.response?.data?.error === "Unauthorized") {
              Swal.fire({
                title: "Your Session is Lost: Please login Back to resume",
                text: ".",
                icon: "error",
                confirmButtonText: "Ok",
                confirmButtonColor: "#152f5f",
              }).then(() => navigate("/"));
            }
          });
      }
    });
  };

  const CancelRequest = (id) => {
    Swal.fire({
      title: "Cancel Credit Request?",
      text: "Are you sure you want to cancel this credit request?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes",
      confirmButtonColor: "#152f5f",
      cancelButtonText: "No",
    }).then((result) => {
      if (result.value) {
        CustomerTermsServices.CancelRequestedCredit(id)
          .then(() => {
            Swal.fire({
              title: "Credit Request Cancelled",
              text: ".",
              icon: "success",
              confirmButtonText: "Ok",
              confirmButtonColor: "#152f5f",
            }).then(() => {
              refreshData();
            });
          })
          .catch((e) => {
            if (e.response?.data?.error === "Unauthorized") {
              Swal.fire({
                title: "Your Session is Lost: Please login Back to resume",
                text: ".",
                icon: "error",
                confirmButtonText: "Ok",
                confirmButtonColor: "#152f5f",
              }).then(() => navigate("/"));
            }
          });
      }
    });
  };

  // Format currency with NumericFormat
  const formatCurrency = (value) => (
    <NumericFormat
      displayType={"text"}
      value={value}
      decimalScale={2}
      fixedDecimalScale={true}
      thousandSeparator={true}
      thousandsGroupStyle={thousandsdisplay}
      className="UI-currency-value"
    />
  );

  // Render pagination controls
  const renderPagination = (totalItems, currentPage, handlePageChange, type) => {
    if (totalItems <= itemsPerPage) return null;

    const totalPages = Math.ceil(totalItems / itemsPerPage);

    return (
      <div className={`UI-pagination-container ${isMobile ? 'UI-pagination-container-mobile' : ''}`}>
        <div className={`UI-pagination-info ${isMobile ? 'UI-pagination-info-mobile' : ''}`}>
          Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} {type}
        </div>
        <div className={`UI-pagination-controls ${isMobile ? 'UI-pagination-controls-mobile' : ''}`}>
          <select
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            className={`UI-page-size-select ${isMobile ? 'UI-page-size-select-mobile' : ''}`}
          >
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
          </select>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === 1 ? 'UI-pagination-button-disabled' : ''} ${hovered === 'first' && currentPage !== 1 ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            onMouseEnter={() => !(currentPage === 1) && setHovered('first')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'First' : 'First'}
          </button>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === 1 ? 'UI-pagination-button-disabled' : ''} ${hovered === 'prev' && currentPage !== 1 ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            onMouseEnter={() => !(currentPage === 1) && setHovered('prev')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Prev' : 'Previous'}
          </button>

          <span className={`UI-pagination-info ${isMobile ? 'UI-pagination-info-mobile' : ''}`}>
            Page {currentPage} of {totalPages}
          </span>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === totalPages ? 'UI-pagination-button-disabled' : ''} ${hovered === 'next' && currentPage !== totalPages ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            onMouseEnter={() => !(currentPage === totalPages) && setHovered('next')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Next' : 'Next'}
          </button>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === totalPages ? 'UI-pagination-button-disabled' : ''} ${hovered === 'last' && currentPage !== totalPages ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            onMouseEnter={() => !(currentPage === totalPages) && setHovered('last')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Last' : 'Last'}
          </button>
        </div>
      </div>
    );
  };

  // Sort indicator component
  const SortIndicator = ({ columnKey, activeSortConfig }) => {
    if (activeSortConfig.key !== columnKey) {
      return <i className="fa fa-sort UI-sort-icon" />;
    }
    return activeSortConfig.direction === 'asc'
      ? <i className="fa fa-sort-up UI-sort-icon UI-sort-active" />
      : <i className="fa fa-sort-down UI-sort-icon UI-sort-active" />;
  };

  const renderCustomerTermsTable = () => {
    const currentPageData = getCurrentPageData();

    if (filteredTerms.length === 0) {
      return (
        <div className={`UI-no-data-message ${isMobile ? 'UI-no-data-message-mobile' : ''}`}>
          {searchTerm ? 'No customer terms found matching your search' : 'No customer terms available'}
        </div>
      );
    }

    return (
      <>
        <div className={`UI-table-container ${isMobile ? 'UI-table-container-mobile' : ''}`}>
          <table className={`UI-table ${isMobile ? 'UI-table-mobile' : ''}`}>
            <thead className="UI-table-header">
              <tr>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'customer' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortTerms('customer')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Customer
                    <SortIndicator columnKey="customer" activeSortConfig={sortConfig} />
                  </div>
                </th>
                <th
                  className={`UI-th UI-th-numeric ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'creditvalue' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortTerms('creditvalue')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Credit Value
                    <SortIndicator columnKey="creditvalue" activeSortConfig={sortConfig} />
                  </div>
                </th>
                <th
                  className={`UI-th UI-th-numeric ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'creditleft' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortTerms('creditleft')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Credit Left
                    <SortIndicator columnKey="creditleft" activeSortConfig={sortConfig} />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'creditperiod' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortTerms('creditperiod')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Credit Period
                    <SortIndicator columnKey="creditperiod" activeSortConfig={sortConfig} />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'creditstatus' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortTerms('creditstatus')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Credit Status
                    <SortIndicator columnKey="creditstatus" activeSortConfig={sortConfig} />
                  </div>
                </th>
                {(appmasterid === "2" || appmasterid === "3") && (
                  <th className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} UI-th-actions`}>Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {currentPageData.map((term) => (
                <tr
                  key={term.id}
                  className={`UI-table-row ${hoveredRow === term.id ? 'UI-table-row-hovered' : ''} ${isMobile ? 'UI-table-row-mobile' : ''} ${term.creditstatus === "Active" ? 'UI-row-active' : 'UI-row-inactive'}`}
                  onMouseEnter={() => setHoveredRow(term.id)}
                  onMouseLeave={() => {
                    setHoveredRow(null);
                    setHoveredAction(null);
                  }}
                >
                  <td className={`UI-td ${hoveredRow === term.id ? 'UI-item-name-hover' : ''} ${isMobile ? 'UI-td-mobile' : ''}`}>
                    {highlightSearchText(term.customer, searchTerm)}
                  </td>
                  <td className={`UI-td UI-numeric ${isMobile ? 'UI-td-mobile' : ''}`}>
                    {formatCurrency(term.creditvalue)}
                  </td>
                  <td className={`UI-td UI-numeric ${isMobile ? 'UI-td-mobile' : ''}`}>
                    <span className={`UI-credit-left ${term.creditleft < term.creditvalue * 0.1 ? 'UI-credit-low' : ''}`}>
                      {formatCurrency(term.creditleft)}
                    </span>
                  </td>
                  <td className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}>
                    <span className="UI-period-badge">
                      {term.creditperiod}
                    </span>
                  </td>
                  <td className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}>
                    <span className={`UI-status-badge ${term.creditstatus === "Active" ? 'UI-status-active' : 'UI-status-inactive'}`}>
                      {term.creditstatus}
                    </span>
                  </td>
                  {(appmasterid === "2" || appmasterid === "3") && (
                    <td className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}>
                      <div className="UI-action-buttons">
                        <Tooltip title="Edit Customer Terms" arrow>
                          <Link
                            to="/newcustomerterms"
                            state={{
                              id: term.id,
                              pagename: "Edit Customer Terms"
                            }}
                            className={`UI-action-button UI-edit-button ${hoveredAction === `edit-${term.id}` ? 'UI-edit-button-hover' : ''} ${isMobile ? 'UI-action-button-mobile' : ''}`}
                            onMouseEnter={() => setHoveredAction(`edit-${term.id}`)}
                            onMouseLeave={() => setHoveredAction(null)}
                          >
                            <img
                              src={EditIcon}
                              alt="Edit"
                              className={`UI-action-icon ${isMobile ? 'UI-action-icon-mobile' : ''} ${hoveredAction === `edit-${term.id}` ? 'UI-action-icon-hover' : ''}`}
                            />
                          </Link>
                        </Tooltip>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {renderPagination(filteredTerms.length, currentPage, handlePageChange, "terms")}
      </>
    );
  };

  const renderCreditRequestsTable = () => {
    const currentPageData = getCurrentPageRequestsData();
    const pendingCount = creditRequests.filter(req => req.status === "Pending").length;

    if (filteredRequests.length === 0) {
      return (
        <div className={`UI-no-data-message ${isMobile ? 'UI-no-data-message-mobile' : ''}`}>
          {searchTermRequests ? 'No credit requests found matching your search' : 'No credit requests available'}
        </div>
      );
    }

    return (
      <>
        {pendingCount > 0 && (
          <div className="UI-pending-alert">
            <i className="fa fa-exclamation-circle"></i>
            You have {pendingCount} pending credit request{pendingCount > 1 ? 's' : ''} to review
          </div>
        )}

        <div className={`UI-table-container ${isMobile ? 'UI-table-container-mobile' : ''}`}>
          <table className={`UI-table ${isMobile ? 'UI-table-mobile' : ''}`}>
            <thead className="UI-table-header">
              <tr>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfigRequests.key === 'customer' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortRequests('customer')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Customer
                    <SortIndicator columnKey="customer" activeSortConfig={sortConfigRequests} />
                  </div>
                </th>
                <th
                  className={`UI-th UI-th-numeric ${isMobile ? 'UI-th-mobile' : ''} ${sortConfigRequests.key === 'reqvalue' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortRequests('reqvalue')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Requested Credit
                    <SortIndicator columnKey="reqvalue" activeSortConfig={sortConfigRequests} />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfigRequests.key === 'requestedby' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortRequests('requestedby')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Requested By
                    <SortIndicator columnKey="requestedby" activeSortConfig={sortConfigRequests} />
                  </div>
                </th>
                <th
                  className={`UI-th UI-th-numeric ${isMobile ? 'UI-th-mobile' : ''} ${sortConfigRequests.key === 'creditvalue' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortRequests('creditvalue')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Current Credit
                    <SortIndicator columnKey="creditvalue" activeSortConfig={sortConfigRequests} />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfigRequests.key === 'approvedby' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortRequests('approvedby')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Approved By
                    <SortIndicator columnKey="approvedby" activeSortConfig={sortConfigRequests} />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfigRequests.key === 'status' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortRequests('status')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Status
                    <SortIndicator columnKey="status" activeSortConfig={sortConfigRequests} />
                  </div>
                </th>
                {(appmasterid === "2" || appmasterid === "3") && (
                  <th className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} UI-th-actions`}>Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {currentPageData.map((request) => (
                <tr
                  key={request.id}
                  className={`UI-table-row ${hoveredRow === request.id ? 'UI-table-row-hovered' : ''} ${isMobile ? 'UI-table-row-mobile' : ''} ${request.status === "Pending" ? 'UI-row-pending' : request.status === "Approved" ? 'UI-row-approved' : 'UI-row-rejected'}`}
                  onMouseEnter={() => setHoveredRow(request.id)}
                  onMouseLeave={() => {
                    setHoveredRow(null);
                    setHoveredAction(null);
                  }}
                >
                  <td className={`UI-td ${hoveredRow === request.id ? 'UI-item-name-hover' : ''} ${isMobile ? 'UI-td-mobile' : ''}`}>
                    {highlightSearchText(request.customer, searchTermRequests)}
                  </td>
                  <td className={`UI-td UI-numeric ${isMobile ? 'UI-td-mobile' : ''}`}>
                    {formatCurrency(request.reqvalue)}
                  </td>
                  <td className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}>
                    {highlightSearchText(request.requestedby, searchTermRequests)}
                  </td>
                  <td className={`UI-td UI-numeric ${isMobile ? 'UI-td-mobile' : ''}`}>
                    {formatCurrency(request.creditvalue)}
                  </td>
                  <td className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}>
                    {request.approvedby || "NA"}
                  </td>
                  <td className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}>
                    <span className={`UI-status-badge ${request.status === "Approved" ? 'UI-status-approved' : request.status === "Pending" ? 'UI-status-pending' : 'UI-status-rejected'}`}>
                      {request.status}
                    </span>
                  </td>
                  {(appmasterid === "2" || appmasterid === "3") && (
                    <td className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}>
                      {request.status === "Pending" ? (
                        approveState[request.id] ? (
                          <div className="UI-approve-form">
                            <TextField
                              type="number"
                              label="Credit Value"
                              value={creditValues[request.id] || request.reqvalue}
                              onChange={(e) => handleCreditValueChange(request.id, e.target.value)}
                              variant="outlined"
                              size="small"
                              error={creditValues[request.id] > request.reqvalue}
                              helperText={creditValues[request.id] > request.reqvalue ? "Exceeds requested value" : ""}
                              className="UI-credit-input"
                            />
                            <div className="UI-approve-buttons">
                              <Tooltip title="Approve" arrow slotProps={{
                                popper: {
                                  sx: {
                                    pointerEvents: 'none',
                                  },
                                },
                              }}>
                                <button
                                  onClick={() => handleApproveCredit(request.id)}
                                  disabled={!creditValues[request.id] || creditValues[request.id] > request.reqvalue}
                                  className={`UI-action-button UI-approve-button ${hoveredAction === `approve-${request.id}` ? 'UI-approve-button-hover' : ''}`}
                                  onMouseEnter={() => setHoveredAction(`approve-${request.id}`)}
                                  onMouseLeave={() => setHoveredAction(null)}
                                >
                                  <img
                                    src={CheckIcon}
                                    alt="Approve"
                                    className="UI-action-icon"
                                  />
                                </button>
                              </Tooltip>
                              <Tooltip title="Cancel" arrow slotProps={{
                                popper: {
                                  sx: {
                                    pointerEvents: 'none',
                                  },
                                },
                              }}>
                                <button
                                  onClick={() => handleClose(request.id)}
                                  className={`UI-action-button UI-cancel-button ${hoveredAction === `cancel-${request.id}` ? 'UI-cancel-button-hover' : ''}`}
                                  onMouseEnter={() => setHoveredAction(`cancel-${request.id}`)}
                                  onMouseLeave={() => setHoveredAction(null)}
                                >
                                  <img
                                    src={CloseIcon}
                                    alt="Cancel"
                                    className="UI-action-icon"
                                  />
                                </button>
                              </Tooltip>
                            </div>
                          </div>
                        ) : (
                          <div className="UI-action-buttons">
                            <Tooltip title="Approve Request" arrow slotProps={{
                              popper: {
                                sx: {
                                  pointerEvents: 'none',
                                },
                              },
                            }}>
                              <button
                                onClick={() => handleApprove(request.id)}
                                className={`UI-action-button UI-approve-button ${hoveredAction === `approve-${request.id}` ? 'UI-approve-button-hover' : ''} ${isMobile ? 'UI-action-button-mobile' : ''}`}
                                onMouseEnter={() => setHoveredAction(`approve-${request.id}`)}
                                onMouseLeave={() => setHoveredAction(null)}
                              >
                                <img
                                  src={CheckIcon}
                                  alt="Approve"
                                  className={`UI-action-icon ${isMobile ? 'UI-action-icon-mobile' : ''} ${hoveredAction === `approve-${request.id}` ? 'UI-action-icon-hover' : ''}`}
                                />
                              </button>
                            </Tooltip>
                            <Tooltip title="Cancel Request" arrow slotProps={{
                              popper: {
                                sx: {
                                  pointerEvents: 'none',
                                },
                              },
                            }}>
                              <button
                                onClick={() => CancelRequest(request.id)}
                                className={`UI-action-button UI-delete-button ${hoveredAction === `cancel-${request.id}` ? 'UI-delete-button-hover' : ''} ${isMobile ? 'UI-action-button-mobile' : ''}`}
                                onMouseEnter={() => setHoveredAction(`cancel-${request.id}`)}
                                onMouseLeave={() => setHoveredAction(null)}
                              >
                                <img
                                  src={CloseIcon}
                                  alt="Cancel"
                                  className={`UI-action-icon ${isMobile ? 'UI-action-icon-mobile' : ''} ${hoveredAction === `cancel-${request.id}` ? 'UI-action-icon-hover' : ''}`}
                                />
                              </button>
                            </Tooltip>
                          </div>
                        )
                      ) : (
                        <span className="UI-action-na">Completed</span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {renderPagination(filteredRequests.length, currentPageRequests, handlePageChangeRequests, "requests")}
      </>
    );
  };

  const pendingCount = creditRequests.filter(req => req.status === "Pending").length;

  return (
    <div>
      <Header />
      <Menu />
      <div className="content-wrapper">
        <div className={`UI-container ${isMobile ? 'UI-container-mobile' : ''}`}>
          <div className="UI-card">
            {/* Header */}
            <div className={`UI-header ${isMobile ? 'UI-header-mobile' : ''}`}>
            <Breadcrumb breadcrumbs={[{name:"Home",path:"/masters"},{name:"Customer Terms",path:null}]}/>

              <div className={`UI-header-content ${isMobile ? 'UI-header-content-mobile' : ''}`}>
                <div>
                  <h1 className={`UI-title ${isMobile ? 'UI-title-mobile' : ''}`}>
                    Customer Terms
                  </h1>
                  <p className={`UI-subtitle ${isMobile ? 'UI-subtitle-mobile' : ''}`}>
                    Manage customer credit terms and requests
                  </p>
                </div>

                <div className={`UI-header-actions ${isMobile ? 'UI-header-actions-mobile' : ''}`}>
                  {/* Mobile Search */}
                  {isMobile && (
                    <input
                      type="text"
                      placeholder={activeTab === "customer-terms" ? "Search customer terms..." : "Search credit requests..."}
                      value={activeTab === "customer-terms" ? searchTerm : searchTermRequests}
                      onChange={(e) => handleSearch(e.target.value)}
                      className="UI-mobile-search-input"
                    />
                  )}

                  <div className={`UI-buttons-row ${isMobile ? 'UI-buttons-row-mobile' : ''}`}>
                    {/* Desktop Search Toggle */}
                    {!isMobile && (
                      <div
                        className={`UI-search-container ${showSearch ? 'UI-search-container-expanded' : ''} ${hovered === "search" ? 'UI-search-container-hover' : ''}`}
                        onClick={() => {
                          if (!showSearch) setShowSearch(true);
                        }}
                        onMouseEnter={() => setHovered("search")}
                        onMouseLeave={() => setHovered(null)}
                      >
                        <i
                          className="fa fa-search UI-search-icon"
                        />
                        {!showSearch && "Search"}
                        {showSearch && (
                          <input
                            type="text"
                            placeholder={activeTab === "customer-terms" ? "Search customer terms..." : "Search credit requests..."}
                            value={activeTab === "customer-terms" ? searchTerm : searchTermRequests}
                            onChange={(e) => handleSearch(e.target.value)}
                            className="UI-search-input"
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                            onBlur={() => {
                              if ((activeTab === "customer-terms" ? searchTerm : searchTermRequests).trim() === "") {
                                setShowSearch(false);
                              }
                            }}
                          />
                        )}
                      </div>
                    )}

                    {/* New Terms button */}
                    {(appmasterid === "2" || appmasterid === "3") && (
                      <Link
                        to="/newcustomerterms"
                        state={{ id: -1, name: "New Terms" }}
                        className={`UI-btn UI-btn-primary ${hovered === "new" ? 'UI-btn-primary-hover' : ''} ${isMobile ? 'UI-btn-mobile' : ''}`}
                        onMouseEnter={() => setHovered("new")}
                        onMouseLeave={() => setHovered(null)}
                      >
                        <i className="fa fa-plus" /> {isMobile ? "New" : "New Terms"}
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs - Using the original style */}
            <div className="d-flex gap-2 m-2">
              <TabButton
                active={activeTab === "customer-terms"}
                onClick={() => setActiveTab("customer-terms")}
              >
                Customer Terms
              </TabButton>

              <TabButton
                active={activeTab === "credit-requests"}
                onClick={() => setActiveTab("credit-requests")}
              >
                Credit Requests
              </TabButton>
            </div>

            {/* Content */}
            {loading ? (
              <div className="UI-loading-container">
                {spin}
              </div>
            ) : (
              <div className="UI-tab-content">
                {activeTab === "customer-terms" ? renderCustomerTermsTable() : renderCreditRequestsTable()}
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CustomerTermsData;